import * as React from 'react'
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  MessageSquareText,
} from 'lucide-react'
import { AudioPlayer } from '@/components/AudioPlayer'
import { AccessLock } from '@/components/AccessLock'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useIsGlobalAdmin } from '@/hooks/useIsGlobalAdmin'
import { cn } from '@/lib/utils'
import { RecordingLoadError } from './RecordingLoadError'
import type { CallRecord, VapiMessage } from '../types'
import type { RecordingType } from '../api'

interface CallLogsRecordingProps {
  call: CallRecord
  messages: VapiMessage[] | null
  resolvedRecordingUrl?: string | null
  resolveRecordingUrl?: (
    recordingType: RecordingType,
    options?: { force?: boolean }
  ) => Promise<string>
  recordingType?: RecordingType | null
  onResolvedRecordingUrlError?: () => void
  isRecordingUrlLoading?: boolean
  /** From `useCallDetail`: the query settled and left no way to get a URL. */
  recordingBlocked?: boolean
  /**
   * Whether this call has playable audio at all. Must come from the *detail*
   * payload, not the calls-list row: the list RPC derives its `audio_url` from
   * the Vapi webhook blob alone, so it is null for a call served from Supabase
   * Storage and this card would render its empty state over a working recording.
   */
  hasRecording: boolean
  /**
   * Hide the Transcript / Messages strip. Set for hand-placed Supabase audio:
   * the turns and events carry Vapi timings, so click-to-seek would jump to the
   * wrong place in a file that was uploaded separately.
   */
  hideTranscriptTabs?: boolean
  /** Re-runs the call-detail query. */
  onRetryDetail?: () => void
  /** Lets the sheet seek playback too (test call notes). */
  seekRef?: React.RefObject<((timeSeconds: number) => void) | null>
}

/** Message roles that only show while the "Tools" toggle is on. */
const TOOL_ROLES = new Set(['tool_calls', 'tool_call_result'])

/**
 * v3 Call Detail Sheet — "Recording" block.
 *
 * Single rounded card containing:
 *   1. Audio player (or empty state when no recording)
 *   2. "Transcript" heading row with a "Tools" toggle and a chevron collapse
 *      toggle on the right (renders when the call has messages — usually
 *      pulled from the Vapi-shaped detail blob via useCallDetail — and
 *      `hideTranscriptTabs` is not set)
 *   3. The transcript turn blobs (per-message event log)
 *
 * The drawer defaults to collapsed; clicking the Transcript title or the
 * chevron toggles it, keeping the sheet header compact and the audio player
 * breathing room. Tool activity (tool_calls / tool_call_result) is opt-in via
 * the "Tools" switch; the System prompt blob renders only for global admins.
 */
export function CallLogsRecording({
  call,
  messages,
  resolvedRecordingUrl,
  resolveRecordingUrl,
  recordingType,
  onResolvedRecordingUrlError,
  isRecordingUrlLoading = false,
  recordingBlocked = false,
  hasRecording,
  hideTranscriptTabs = false,
  onRetryDetail,
  seekRef: sharedSeekRef,
}: CallLogsRecordingProps) {
  const [expanded, setExpanded] = React.useState(false)
  const [showTools, setShowTools] = React.useState(false)
  const isGlobalAdmin = useIsGlobalAdmin()

  const hasMessages = messages !== null && messages.length > 0
  // Drives the strip AND the divider above it — gating only one leaves a border
  // with nothing under it.
  const showTranscriptTabs = hasMessages && !hideTranscriptTabs
  // What bounds this is `recordingBlocked` below, not the resolver check: the
  // hook raises that as soon as the detail query settles with no recording type
  // — error, 404, or a payload with every recording field null — so reaching
  // here with no type can only mean the fetch is still in flight. The resolver
  // check just covers a caller that passes no resolver at all.
  const isAwaitingRecordingType = Boolean(resolveRecordingUrl) && !recordingType

  const visibleMessages = React.useMemo(() => {
    if (!messages) return []
    return messages.filter((msg) => {
      // System prompt is restricted to global admins.
      if (msg.role === 'system') return isGlobalAdmin
      // Tool activity is opt-in via the "Tools" toggle.
      if (TOOL_ROLES.has(msg.role)) return showTools
      return true
    })
  }, [messages, isGlobalAdmin, showTools])

  // Written by AudioPlayer (same contract as StereoWaveformPlayer.seekRef);
  // clicking a transcript turn jumps playback to that turn's start time.
  const ownSeekRef = React.useRef<((timeSeconds: number) => void) | null>(null)
  const seekRef = sharedSeekRef ?? ownSeekRef

  return (
    <div className="bg-neutral-ui-bg shadow-[var(--shadow-md)] overflow-hidden rounded-lg border border-neutral-ui-border">
      <div
        className={cn(
          'bg-app',
          // Divider below the player only when the heading row follows.
          showTranscriptTabs && 'border-b border-neutral-ui-border'
        )}>
        {hasRecording ? (
          <div className="p-3">
            {/* Nothing here is ever handed a stored URL directly: the raw R2 URL
                the list RPC returns stopped being playable when VAPI locked down
                public access, so `hasRecording` is a flag and the player asks
                `resolveRecordingUrl` for something signed. */}
            {recordingBlocked ? (
              <RecordingLoadError onRetry={onRetryDetail} />
            ) : (
              <AudioPlayer
                key={call.call_id}
                resolvedRecordingUrl={resolvedRecordingUrl}
                resolveRecordingUrl={
                  resolveRecordingUrl && recordingType
                    ? (options) => resolveRecordingUrl(recordingType, options)
                    : undefined
                }
                onResolvedRecordingUrlError={onResolvedRecordingUrlError}
                isLoading={isRecordingUrlLoading || isAwaitingRecordingType}
                seekRef={seekRef}
              />
            )}
          </div>
        ) : (
          <div className="bg-neutral-subtle-bg px-3 py-6 text-center text-sm text-muted-foreground">
            No recording available for this call.
          </div>
        )}
      </div>

      {showTranscriptTabs && (
        <>
          <div className="flex items-center gap-3 px-3">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className={cn(
                'inline-flex items-center gap-2 py-2.5 text-sm',
                expanded
                  ? 'text-primary-high-contrast font-medium'
                  : 'text-neutral-low-contrast hover:text-primary-high-contrast'
              )}>
              <MessageSquareText className="h-4 w-4" aria-hidden />
              Transcript
            </button>
            <label className="text-neutral-low-contrast ml-auto flex cursor-pointer items-center gap-2 text-sm">
              <Switch
                size="sm"
                checked={showTools}
                onCheckedChange={setShowTools}
                aria-label="Show tool calls"
              />
              Tools
            </label>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? 'Collapse drawer' : 'Expand drawer'}
              aria-expanded={expanded}
              className="text-neutral-low-contrast hover:bg-transparent hover:text-primary-high-contrast">
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>

          {expanded && (
            <div className="bg-primary-subtle-bg p-3">
              <SheetMessagesList
                messages={visibleMessages}
                customerLabel={call.patient_name}
                // No player, no seeking — leave the turns inert rather than
                // rendering them as buttons that do nothing. `recordingBlocked`
                // replaces the player with the error surface, so nothing ever
                // writes seekRef in that state.
                onMessageClick={
                  call.audio_url && !recordingBlocked
                    ? (seconds) => seekRef.current?.(seconds)
                    : undefined
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** "0:12" / "1:34" — always m:ss, never the bare-seconds form. */
function formatSeconds(secs: number | undefined): string {
  if (secs === undefined) return '—'
  const m = Math.floor(secs / 60)
  const s = Math.floor(secs % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Sheet transcript speaker colors — intentionally distinct from the
 * standalone Call Detail page's pink/blue scheme (see TranscriptTurn.tsx):
 * Agent turns use the shared `angelica` brand tint, Customer turns stay
 * neutral. #880 briefly repainted this drawer to match the waveform
 * player's pink/blue channel colors, but that was out of scope — the
 * click-to-seek request in #881 didn't ask for a recolor — so it was
 * reverted back to these original colors.
 *
 * The customer bubble also gets a `neutral-ui-border` outline (the same
 * neutral-card treatment `SheetSectionCard`/`VisitsSheetSectionCard` use) —
 * its `bg-app` fill is nearly identical to this tab's own
 * `bg-primary-subtle-bg` background, so without a border the resting row
 * had no visible card at all.
 */
const AGENT_BUBBLE_BG = 'bg-angelica-hover-bg'
const AGENT_LABEL_TEXT = 'text-angelica-low-contrast'
const CALLEE_BUBBLE_BG = 'bg-app border border-neutral-ui-border'
const CALLEE_LABEL_TEXT = 'text-neutral-low-contrast'

/**
 * Background tint, label text color, and display label per message role.
 * Tool events share the primary (blue) tint; unknown roles fall back to a
 * neutral row with the raw role as its label.
 */
const MESSAGE_ROLE_STYLE: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  system: {
    bg: 'bg-david-hover-bg',
    text: 'text-david-low-contrast',
    label: 'System',
  },
  assistant: {
    bg: AGENT_BUBBLE_BG,
    text: AGENT_LABEL_TEXT,
    label: 'Angelica',
  },
  bot: {
    bg: AGENT_BUBBLE_BG,
    text: AGENT_LABEL_TEXT,
    label: 'Angelica',
  },
  user: {
    bg: CALLEE_BUBBLE_BG,
    text: CALLEE_LABEL_TEXT,
    label: 'Customer',
  },
  tool_calls: {
    bg: 'bg-primary-hover-bg',
    text: 'text-primary-low-contrast',
    label: 'tool_calls',
  },
  tool_call_result: {
    bg: 'bg-primary-hover-bg',
    text: 'text-primary-low-contrast',
    label: 'tool_call_result',
  },
}

/** Conversational turns whose expanded view renders prose instead of JSON. */
const CONVERSATIONAL_ROLES = new Set(['assistant', 'bot', 'user'])

/**
 * Compact per-turn event log for the sheet's Transcript drawer. Each row:
 * seconds-from-start (mono, gray) → role label (colored per
 * MESSAGE_ROLE_STYLE, with the customer's real name on `user` turns when
 * known) → message preview. Click a row to expand: conversational turns
 * (assistant/bot/user) reveal the full message as readable prose, while
 * system and tool events reveal the full JSON. The System row carries a lock
 * marker — it's only ever rendered for global admins (the caller filters it
 * out otherwise).
 *
 * When `onMessageClick` is set, clicking a conversational turn ALSO seeks
 * the recording to that turn's start time (same behavior as `TranscriptTurn`
 * on the Call Detail page) — one click both expands the turn and jumps
 * playback to it. Tool/system rows only expand. Rows are real buttons, so
 * Enter/Space keyboard activation comes free.
 */
function SheetMessagesList({
  messages,
  customerLabel,
  onMessageClick,
}: {
  messages: VapiMessage[]
  customerLabel?: string | null
  onMessageClick?: (secondsFromStart: number) => void
}) {
  // Keyed by message identity rather than list index so expansion follows the
  // same message when the Tools/admin filters reindex the list; a message
  // that gets filtered out simply collapses instead of handing its expanded
  // state to whichever row inherits its index.
  const [expandedMsg, setExpandedMsg] = React.useState<VapiMessage | null>(null)

  if (messages.length === 0) {
    return (
      <p className="text-muted-foreground py-4 text-center text-sm">
        No messages available.
      </p>
    )
  }

  return (
    <div className="min-w-0 space-y-2">
      {messages.map((msg, i) => {
        const isExpanded = expandedMsg === msg
        const style = MESSAGE_ROLE_STYLE[msg.role] ?? {
          bg: 'bg-app',
          text: 'text-neutral-low-contrast',
          label: msg.role,
        }
        const label =
          msg.role === 'user'
            ? customerLabel?.trim() || style.label
            : style.label
        return (
          <button
            type="button"
            key={i}
            onClick={() => {
              setExpandedMsg(isExpanded ? null : msg)
              // Conversational turns double as click-to-seek targets;
              // tool/system rows only expand.
              if (
                onMessageClick &&
                CONVERSATIONAL_ROLES.has(msg.role) &&
                Number.isFinite(msg.secondsFromStart)
              ) {
                onMessageClick(msg.secondsFromStart)
              }
            }}
            className={cn(
              'flex w-full cursor-pointer gap-3 rounded-xl p-3 text-left transition-colors',
              style.bg
            )}>
            <span className="text-neutral-low-contrast w-8 shrink-0 pt-0.5 text-sm tabular-nums">
              {formatSeconds(msg.secondsFromStart)}
            </span>
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'flex items-center gap-1.5 text-sm font-medium',
                  style.text
                )}>
                {label}
                {msg.role === 'system' && (
                  <AccessLock className="h-3.5 w-3.5" />
                )}
              </p>
              {isExpanded ? (
                CONVERSATIONAL_ROLES.has(msg.role) ? (
                  <p className="text-primary-high-contrast mt-0.5 text-sm leading-relaxed whitespace-pre-wrap">
                    {msg.message || msg.name || '—'}
                  </p>
                ) : (
                  <pre className="bg-app text-primary-high-contrast border-neutral-subtle-border mt-2 max-h-[32rem] overflow-y-auto rounded-md border p-3 font-mono text-xs whitespace-pre-wrap break-all">
                    {JSON.stringify(msg, null, 2)}
                  </pre>
                )
              ) : (
                <p className="text-primary-high-contrast mt-0.5 line-clamp-3 text-sm leading-relaxed">
                  {msg.message || msg.name || '—'}
                </p>
              )}
            </div>
            <ChevronRight
              aria-hidden
              className={cn(
                'text-neutral-low-contrast mt-1 h-4 w-4 shrink-0 transition-transform',
                isExpanded && 'rotate-90'
              )}
            />
          </button>
        )
      })}
    </div>
  )
}
